import { AuthService, User } from '@auth0/auth0-angular';
import { Component, OnInit } from '@angular/core';
import { Router, NavigationStart } from '@angular/router';
import { LogError, Metadata } from '@memberjunction/core';
import { setupGraphQLClient, GraphQLProviderConfigData } from '@memberjunction/graphql-dataprovider';
import { AskSkipMetadataEngine, LoadGeneratedEntities } from 'mj_generatedentities'
import { environment } from '../environments/environment';
import { SharedService } from './core/services/shared-service';
import { filter, lastValueFrom, take } from 'rxjs';

import { ViewEncapsulation } from '@angular/core';
import { UserContextService } from './core/services/user-context.service';
import { InstanceProviderService } from './dashboard/instance-provider.service';
import { buildRoutePrefix } from './core/util';


LoadGeneratedEntities(); // forces the generated entities library to load up, sometimes tree shaking in the build process can break this, so this is a workaround that ensures it always happens
export type RefreshTokenFunction = () => Promise<string>;
export const DEFAULT_SUBDOMAIN = 'default';

@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
})
export class AppComponent implements OnInit {
  title = environment.APP_TITLE;

  isSetupComplete: boolean = false;
  isAuthenticated: boolean = false;
  isLoading: boolean = false;
  inSystemAdminMode: boolean = false;

  constructor(
    private router: Router,
    public auth: AuthService,
    public sharedService: SharedService,
    private userContextService: UserContextService,
    private instanceProvider: InstanceProviderService,
  ) {
    // Subscribe to the isAuthenticated$ observable
    this.auth.isAuthenticated$.subscribe(authenticated => {
      this.isAuthenticated = authenticated;
    });

    this.sharedService.globalLoading$.subscribe(loading => {
      this.isLoading = loading;
    });

    this.userContextService.SystemAdminMode$.subscribe(mode => {
      this.inSystemAdminMode = mode;
    });
  }

  private _pendingURL: string = '';
  async ngOnInit() {
    this.router.events
    .pipe(
      filter(event => event instanceof NavigationStart),
      take(1)
    )
    .subscribe((event: NavigationStart) => {
      this._pendingURL = event.url;
      console.log('Activated Route URL:', event.url);
      this.setupAuth();
    });
  }

  public doSignUp() {
    this.auth.loginWithRedirect({
      authorizationParams: {
        screen_hint: 'signup',
      },
    });
  }

  public doLogin() {
    this.auth.loginWithRedirect()
  }
  
  async handleLogin(token: string, claims: any, profile: User) {
    if (token) {
      const url: string = environment.GRAPHQL_URI;
      const wsurl: string = environment.GRAPHQL_WS_URI;
 
      try {
        const start = new Date();
        const refreshTokenFunction: RefreshTokenFunction = async () => {return 'NoToken'}
        const config = new GraphQLProviderConfigData(token, url, wsurl, refreshTokenFunction)
        await setupGraphQLClient(config);
        const end = new Date();
        const elapsed = (end.getTime() - start.getTime()) / 1000;
        console.log(`setupGraphQLClient() took ${elapsed} seconds`);

        // Check to see if the user has access... 
        const md = new Metadata();
        if (!md.CurrentUser) {
          console.log('User not available in metadata');
          this.doAccessDenied();
        }
        else {
          // now do the data setup for the app
          const setupStart = new Date();
          await this.sharedService.doSetup();
          await AskSkipMetadataEngine.Instance.Config(true, md.CurrentUser); // Too much data is cached.
          const setupEnd = new Date();
          const setupElapsed = (setupEnd.getTime() - setupStart.getTime()) / 1000;
          console.log(`setupService.doSetup() took ${setupElapsed} seconds`);

          if (md.Entities.length > 0) {
            // now await for the activated Route to give us the initial route
            await this.userContextService.LoadMetaData(profile, this._pendingURL);// Load the organizational data first

            // Mark this after any potential redirect from the above code
            // Mark the application as initialized
            this.sharedService.markAppInitialized();
            this.isSetupComplete = true;
            
            // @jordanfanapour - you had this set to InstanceConnectorsLoaded - but that is too restrictive because you end up throwing peple with test instances, like us, into a loop
            // and we can't get out of it in the UI, for example the localhosts don't have defined connectors, so that's not good. Changed this back to below
            if (this.userContextService.OnboardingState === 'Complete') {
              // check the URL and see if we have any instance information in it, if we don't before we load the instance provider we should set the URL
              // to the instance URL
              const instance = this.userContextService.CurrentInstance;
              // const currentURL = this.router.url;
              const currentURL = this._pendingURL;
              if (currentURL.indexOf('/instance/' + instance.ID) === -1 && currentURL.indexOf('/settings/') === -1 && currentURL.indexOf('/onboarding/') === -1) {
                // we need to redirect to the instance URL since we are loading it
                const newRouteURL = buildRoutePrefix(instance.ID);
                this.router.navigate([newRouteURL, 'chat']);
              }
              this.sharedService.markGlobalLoading(false);
              await this.instanceProvider.SetupInstanceProvider(); // then load the instance provier which uses the metadata from the above
            } else {
              await this.userContextService.CheckOnboardingAndGoToStage();
            }
          }
        }
      } 
      catch (err) {
        LogError(err);
        this.isSetupComplete = false;
        this.doAccessDenied(err);
      }
    }
  }

  private async doAccessDenied(err: any = null) {
    if (err) {
      const retryKey = 'auth-retry-dt';
      const lastRetryDateTime = localStorage.getItem(retryKey);
      const hourBefore = +new Date(Date.now() - 60 * 60 * 1000);
      const retriedRecently = lastRetryDateTime && +new Date(lastRetryDateTime) > hourBefore;
      const expiryError =  (err as any)?.response?.errors?.[0]?.message.includes('Access denied');
      
      if (!retriedRecently && expiryError) {
        LogError('JWT Expired, retrying once: ' + err);
        localStorage.setItem(retryKey, new Date().toISOString());
        const login$ = this.auth.loginWithRedirect();
        await lastValueFrom(login$);
      } else {
        this.sharedService.AccessDenied = true;
        this.sharedService.DisplayNotification(err, 'error');
      }
    } else {
      this.sharedService.AccessDenied = true;
      this.sharedService.DisplayNotification("You don't have access to the application, contact us for help.", 'error');
    }
  }

  private _Authenticated: boolean = false;
  public IsAuthenticated(): boolean {
    return this._Authenticated;
  }

  async setupAuth() { 
    this.auth.user$.subscribe(
      (profile) => {
        if (profile && profile.email) {
          console.log('User profile is available', profile);

          this.auth.idTokenClaims$.subscribe((claims) => {
              if (claims && claims.__raw) {
                this.handleLogin(claims?.__raw, claims, profile);
              }
            }
          );
        } else {
          console.log('User profile is not available');
        }
      }
    );   
  }

  isLoggedIn(): boolean {
    return this.sharedService.LoggedIn;
  }
}
